import type { RouteType } from "types/RouteType";
import type { ServiceStoreConfig } from "types/ServiceStoreTypes";
import type RootStore from "stores/RootStore";
import { kebabToCamel, kebabToTitle } from "../../../components/ServiceView/utils";

/**
 * Generates dynamic routes for service stores that have ServiceView enabled
 * Routes are created under /management/data/[service-name]
 * @param serviceStores Array of service store configurations
 * @returns Array of RouteType objects for ServiceView-enabled stores
 */
export function generateServiceViewRoutes(
  serviceStores: ServiceStoreConfig[]
): RouteType[] {
  return serviceStores
    .filter(store => store.options?.serviceView?.enableServiceView)
    .map(store => {
      const permissions = store.options?.serviceView?.permissions;
      const serviceName = store.name;
      const title = kebabToTitle(serviceName);

      return {
        name: `${title} Management`,
        path: `/management/data/${serviceName}`,
        layout: "/app",
        viewportCard: {
          header: {
            title: `${title} Management`,
            component: () => import("components/ServiceView/ServiceView.Header"),
            enableRefreshButton: true,
            enableBackButton: true,
            color: "primary",
          },
          body: {
            grayBackground: true,
            component: () => import("components/ServiceView/ServiceView.Body"),
          },
        },
        dialogs: [
          {
            name: "ServiceViewCsvExport",
            title: "Export to CSV",
            component: () => import("components/ServiceView/dialogs/ServiceViewCsvExport"),
            cancelButton: false,
            height: 600,
          },
          {
            name: "ServiceViewPdfExport",
            title: "Export to PDF",
            component: () => import("components/ServiceView/dialogs/ServiceViewPdfExport"),
            cancelButton: false,
            height: 700,
          },
          {
            name: "ServiceViewCreateEntity",
            title: "Create New Entity",
            component: () => import("components/ServiceView/dialogs/ServiceViewCreateEntity"),
            cancelButton: false,
            height: 600,
          },
        ],
        group: "Management",
        requiresAuth: permissions?.requiresAuth ?? true,
        requiresAdmin: permissions?.requiresAdmin ?? true,
        requiresPermission: permissions?.requiresPermission ? true : false,
        beforeEnter: async ({ rootStore }: { rootStore: RootStore }) => {
          // The service name is in the route definition
          const currentServiceName = serviceName;
          console.log('ServiceViewRouteGenerator beforeEnter for', currentServiceName);

          // Pre-load the service data
          const storeName = kebabToCamel(currentServiceName) + "Store";
          const serviceStore = (rootStore as Record<string, unknown>)[storeName];

          if (serviceStore && typeof serviceStore === "object" && "find" in serviceStore) {
            try {
              const findMethod = serviceStore.find as (params: { query: { $limit: number } }) => Promise<unknown>;
              await findMethod({ query: { $limit: 100 } });
            } catch (err) {
              console.error(`Failed to load data for ${currentServiceName}:`, err);
            }
          } else {
            console.warn(`Service store ${storeName} not found or invalid.`);
          }

          return true;
        },
      };
    });
}

/**
 * Utility to convert service path to store name
 * Used in route generation and components
 * @param servicePath The service path (e.g., "purchase-orders")
 * @returns The store name (e.g., "purchaseOrdersStore")
 */
export function servicePathToStoreName(servicePath: string): string {
  return kebabToCamel(servicePath) + "Store";
}